import uniq from 'lodash/uniq';
import map from 'lodash/map';
import union from 'lodash/union';

import { variantWithSku } from 'common/selectors';

export const getLineItemsProcessing = state => state.lineItemsProcessing;

export const getLineItemProcessing = (state, lineItem) => {
  return (
    state.lineItemsProcessing.find(
      item => item.offeringId === lineItem.variant_offering_id
    ) || null
  );
};

export const getLineItemProcessingQuantity = (lineItem, state) => {
  const lineItemProcessing = getLineItemProcessing(state, lineItem);
  return lineItemProcessing ? lineItemProcessing.quantity : null;
};

export const isLineItemSaving = (lineItem, state) => {
  return state.variantsSaving.some(
    item => item.sku === lineItem.sku && item.offeringId === lineItem.offeringId
  );
};

export const productWithId = (id, state) => {
  return state.products.find(v => v.id === id);
};

export const savedItemWithSku = (sku, state, offeringId) => {
  return orderLineItemWithSku(state.currentOrder, sku, offeringId);
};

export const orderLineItemWithSku = (order, sku, offeringId) => {
  return order.line_items.find(
    item => item.sku === sku && item.variant_offering_id === offeringId
  );
};

export const hasOrderLineItemWithOfferingId = (order, offeringId) => {
  return order.line_items.some(item => item.variant_offering_id === offeringId);
};

export const getSidebarFocusState = state => {
  return state.sidebarIsFocused;
};

export const getCartOpenState = state => {
  return state.isOpen;
};

export const getSubscriptionConflict = state => {
  return state.userIsSubscribing && !state.canBecomeMember;
};

export const getShippingCost = ({
  currentOrder,
  freeShippingAmount,
  standardShippingCost,
}) => {
  const itemTotalCents = parseFloat(currentOrder.item_total) * 100;
  const hasFreeShipping =
    currentOrder.membership_order ||
    (freeShippingAmount && itemTotalCents >= freeShippingAmount);
  return hasFreeShipping ? 0 : standardShippingCost;
};

export const getPendingItemQuantityDiff = (pendingItem, state) => {
  const savedLineItem = savedItemWithSku(
    pendingItem.sku,
    state,
    pendingItem.offeringId
  );
  const savedQuantity = savedLineItem ? savedLineItem.quantity : 0;
  return pendingItem.quantity - savedQuantity;
};

export const getPendingQuantity = state => {
  return state.lineItemsPending.reduce((total, item) => {
    return total + getPendingItemQuantityDiff(item, state);
  }, 0);
};

export const getPendingQuantitiesByLineItem = state => {
  const pendingQuantities = state.lineItemsPending.map(item => {
    return {
      sku: item.sku,
      quantity: getPendingItemQuantityDiff(item, state),
      offeringId: item.offeringId,
    };
  });

  return pendingQuantities.length ? pendingQuantities : 0;
};

export const getOrderLineItemQuantityDiff = (orderPrev, orderNew) => {
  // Map over the orders and grab the indivdual skus and corresponding offering ids
  // Calling toString on the values so we can more easily get a uniq array in the
  // following lines
  const offeringsPrev = map(orderPrev.line_items, item =>
    [item.sku, item.variant_offering_id].toString()
  );
  const offeringsNew = map(orderNew.line_items, item =>
    [item.sku, item.variant_offering_id].toString()
  );

  // Combine the offerings from both new and old orders to get a uniq list of items from both
  const offerings = uniq(union(offeringsPrev, offeringsNew));

  return offerings.map(offer => {
    // Split the offer from a string to an array so we can use the
    // id and sku
    offer = offer.split(',');
    const sku = offer[0];
    const offeringId = parseInt(offer[1]);

    // Set up and return the line items with their quantities
    const lineItemDefault = { sku, offeringId, quantity: 0 };
    const lineItemPrev =
      orderLineItemWithSku(orderPrev, sku, offeringId) || lineItemDefault;
    const lineItemNew =
      orderLineItemWithSku(orderNew, sku, offeringId) || lineItemDefault;
    const quantityDiff = lineItemNew.quantity - lineItemPrev.quantity;
    return { sku, offeringId, quantityDiff, lineItemNew, lineItemPrev };
  });
};

export const getPendingPrice = state => {
  const { membershipDiscountRate, currentOrder, lineItemsPending } = state;
  const isMembershipOrder = currentOrder.membership_order;
  return lineItemsPending.reduce((total, item) => {
    const variant = variantWithSku(item.sku, state);
    const hasMembershipDiscount = isMembershipOrder && variant.is_subscribable;
    const discountRate = hasMembershipDiscount ? 1 - membershipDiscountRate : 1;
    const pricePerItem = (variant.price * discountRate).toFixed(2);
    return total + pricePerItem * getPendingItemQuantityDiff(item, state);
  }, 0);
};

export const getTotalSavings = state => {
  const lineItems = state.currentOrder.line_items;
  // Sometimes state.currentOrder.line_items is undefined so we need to catch
  // that and then report to bugsnag to diagnose
  if (lineItems === undefined) {
    reportMissingLineItems(state);
    return 0;
  }
  return getTotalSavingsForLineItems(lineItems, state);
};

export const getTotalSavingsForLineItems = (lineItems, state) => {
  return lineItems.reduce((total, lineItem) => {
    return total + getLineItemSavings(lineItem, state);
  }, 0);
};

export const getLineItemsAtCartOpen = state => {
  const order = { ...state.currentOrder };
  const lineItems = order.line_items.slice();
  if (state.currentOrderAtCartOpen) {
    // we restore any 0 quantity line_items that existed
    // when the cart was first opened so that the user can
    // correct an accidental additional tap
    state.currentOrderAtCartOpen.line_items.forEach((lineItemOrig, index) => {
      if (!lineItems.find(item => item.sku === lineItemOrig.sku)) {
        lineItems.splice(index, 0, {
          ...lineItemOrig,
          quantity: 0,
        });
      }
    });
  }
  return lineItems;
};

export const getLineItemSavings = lineItem => {
  const promoTotal = Math.abs(parseFloat(lineItem.promo_total));
  const adjustmentTotal = getLineItemCommitmentRewardAdjustmentTotal(lineItem);

  return getLineItemAutoShipDiscount(lineItem) + promoTotal + adjustmentTotal;
};

export const getLineItemCommitmentRewardAdjustmentTotal = lineItem => {
  return Math.abs(parseFloat(lineItem.commitment_reward_adjustment_total));
};

export const getLineItemAutoShipDiscount = lineItem => {
  return Math.abs(parseFloat(lineItem.membership_discount_total));
};

export const getLineItemsSubscribable = state => {
  return getLineItemsWithSubscribableValue(state, true);
};

export const getLineItems = state => {
  return state.currentOrder.line_items;
};

export const getLineItemsRegular = state => {
  return getLineItemsWithSubscribableValue(state, false);
};

export const getLineItemsSubscribableQuantity = state => {
  return getLineItemsTotalQuantity(getLineItemsSubscribable(state));
};

export const getLineItemsWithSubscribableValue = (state, value) => {
  return state.currentOrder.line_items.filter(item => {
    const variant = variantWithSku(item.sku, state);
    return variant && variant.is_subscribable === value;
  });
};

export const getLineItemsTotalQuantity = lineItems => {
  return lineItems.reduce((total, item) => {
    return total + item.quantity;
  }, 0);
};

export const getTotalPriceOfLineItems = (lineItems, state) => {
  return lineItems.reduce((total, lineItem) => {
    return (
      total +
      lineItem.price * lineItem.quantity -
      getLineItemSavings(lineItem, state)
    );
  }, 0);
};

// second argument allows us to easily mock the selector
export const getNumOfSubscribableItemsReqToCheckout = (
  state,
  subscribableQuantityFn = getLineItemsSubscribableQuantity
) => {
  const { minimumSubscriptionItems } = state;
  const isMembershipOrder = state.currentOrder.membership_order;
  const hasSubscriptionCommitment =
    state.currentOrder.has_subscription_commitment;
  const subscribableQuantity = subscribableQuantityFn(state);
  if (isMembershipOrder || hasSubscriptionCommitment) {
    return Math.max(minimumSubscriptionItems - subscribableQuantity, 0);
  }
  return 0;
};

export const getLineItemQuantitySaved = (state, props) => {
  const { currentOrder } = state;
  const lineItem = currentOrder.line_items.find(
    item => item.variant_offering_id === props.offeringId
  );
  return lineItem ? lineItem.quantity : 0;
};

export const getLineItemQuantityPending = (state, props) => {
  const { lineItemsPending } = state;
  const lineItemPending = lineItemsPending.find(
    item => item.offeringId === props.offeringId
  );
  return lineItemPending
    ? lineItemPending.quantity
    : getLineItemQuantitySaved(state, props);
};

export const getVariantForVariantOffering = (variantOffering, state) => {
  return variantWithSku(variantOffering.variantSku, state);
};

export const getOfferingName = (variantOffering, state) => {
  const variant =
    variantOffering && variantWithSku(variantOffering.variantSku, state);
  return variant && variant.name;
};

export const getLineItemWithVariantOffering = (variantOffering, state) => {
  const lineItems = state.currentOrder.line_items;
  return lineItems.find(
    lineItem => lineItem.variant_offering_id === variantOffering.id
  );
};

export const isConvertOrderToSubscriptionProcessing = (order, state) => {
  if (state.convertingOrderToSubscription) {
    return order.number === state.convertingOrderToSubscription.number;
  }
};

export const getShouldAutoShowCommitmentUpsellModal = (stateNew, statePrev) => {
  const { evergreenOffering } = stateNew;

  // short circuit so all code below can assume we have the object
  if (!evergreenOffering) {
    return false;
  }

  const { isSoldOut } = evergreenOffering;
  const isAlreadyInCart = !!getLineItemWithVariantOffering(
    evergreenOffering,
    stateNew
  );

  const orderPrev = statePrev.currentOrder;
  const orderNew = stateNew.currentOrder;

  const isTransitioningToMembershipOrder =
    !orderPrev.membership_order && orderNew.membership_order;
  return !isSoldOut && !isAlreadyInCart && isTransitioningToMembershipOrder;
};

export const reportMissingLineItems = state => {
  const error = new Error('state.currentOrder.line_items is undefined');

  if (typeof bugsnagClient !== 'undefined')
    bugsnagClient.notify(error, {
      metaData: {
        reduxState: state,
      },
    });
};
